import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaSearch,
  FaLock,
  FaCheckCircle,
  FaBolt,
  FaShieldAlt,
  FaUsers,
  FaUserCircle,
  FaArrowRight,
  FaArrowLeft,
  FaWallet,
  FaIdCard,
  FaInfoCircle,
  FaBuilding,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { Tooltip } from "antd";
import {
  initiateVerificationRequest,
  completeVerificationRequest,
  fetchUserProfile,
} from "../../redux/actions";
import { apiPostInternalCall, apiGet } from "../../apiUtils";
import {
  getPricingSegment,
  savePricingSegmentFromCurrency,
} from "../../pricingSegment";
import { initiatePaystackPayment } from "../../services/paystackService";
import baseUrl from "../../apiConfig";
import paystackLogo from "../../images/paystack.png";
import flutterwaveLogo from "../../images/flutterwave-logos-idVM8GW1LQ.png";
import verificationConfig from "./verificationConfig";
import RecommendedOffers from "../../components/ads/RecommendedOffers";
import {
  SampleResultContent,
  VehicleSampleReport,
  sampleData,
} from "../../components/SampleResultPopup/SampleResultPopup";
import PdfModal from "../../components/PdfModal/PdfModal";
import termsPdf from "../../images/e-citizen_Nigeria_Terms_of_Service_v2.1_Confirmed.pdf";
import privacyPdf from "../../images/e-citizen_Nigeria_Privacy_Notice_v2.1_Confirmed.pdf";
import {
  RECORD_NOT_FOUND_MESSAGE,
  RECORD_NOT_FOUND_TITLE,
  isRecordNotFoundDetail,
} from "../../constants/verificationMessages";
import {
  trackBeginCheckout,
  trackFormSubmit,
  trackPaymentFailed,
  trackPaymentInitiated,
  trackVerificationStarted,
  trackPurchase,
} from "../../analytics/analytics";
import { withAnalyticsMetadata } from "../../analytics/attribution";

import {
  PageWrapper,
  HeroSection,
  Breadcrumb,
  HeroInner,
  HeroText,
  HeroTitle,
  HeroSubtitle,
  HeroImage,
  StepperWrapper,
  StepperInner,
  StepItem,
  StepCircle,
  StepLabel,
  StepLine,
  ContentWrapper,
  SearchGrid,
  FormCard,
  FormCardTitle,
  FormCardSub,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  CharCounter,
  FormActions,
  ClearBtn,
  ContinueBtn,
  SidebarCard,
  SidebarTitle,
  SidebarItem,
  SidebarNote,
  YouWillGetRow,
  YouWillGetItem,
  YouWillGetCard,
  YouWillGetTitle,
  PriceLabel,
  PriceAmount,
  PriceBreakdown,
  PriceRow,
  PriceTotalRow,
  IdTypeDisplay,
  PaymentGrid,
  PaymentMethodsCard,
  PaymentMethodTitle,
  PaymentMethodSub,
  PaymentOption,
  PaymentOptionLabel,
  SummaryCard,
  SummaryTitle,
  SummaryAmount,
  SummaryHeader,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  PayBtn,
  SecuredBy,
  SampleSection,
  SampleHeader,
  SampleTitle,
  SampleSub,
  SampleBadge,
  TrustBar,
  TrustBarInner,
  TrustItem,
  TrustIcon,
  TrustText,
  TrustTitle,
  TrustDesc,
  ProcessingWrapper,
  ProcessingSpinner,
  ProcessingText,
  ProcessingSub,
  PopupOverlay,
  PopupCard,
  PopupHeader,
  PopupMeta,
  PopupIcon,
  PopupTitle,
  PopupSubtitle,
  PopupCloseButton,
  PopupBody,
  PopupCountBanner,
  PopupBusinessList,
  PopupBusinessItem,
  PopupBusinessIcon,
  PopupBusinessInfo,
  PopupBusinessName,
  PopupBusinessMeta,
  PopupRow,
  PopupField,
  PopupFieldLabel,
  PopupFieldValue,
  PopupActionRow,
  ResultCardPopup,
  ResultTopPopup,
  ResultPhotoPopup,
  ResultGridPopup,
  ResultFieldPopup,
  ResultLabelPopup,
  ResultValuePopup,
  VerifiedBadgePopup,
  ResultFooterPopup,
  ResultDisclaimerPopup,
  ErrorAlert,
} from "./VerifyPage.elements";

// Steps are now dynamic — defined inside the component based on config.requiresConsent

// Maps each verify page type to the matching landing-page sample result type
const SAMPLE_POPUP_TYPE_MAP = {
  nin: "nin",
  phone: "phone",
  business: "business",
  "business-name": "business",
  bvn: "financial",
  vehicle: "vehicle",
};

const PAYMENT_METHODS = [
  {
    id: "wallet",
    label: "Wallet",
    icon: FaWallet,
    paymentType: "WALLET",
  },
  {
    id: "flutterwave",
    label: "",
    icon: null,
    paymentType: "INSTANT",
  },
  {
    id: "paystack",
    label: "Paystack",
    icon: null,
    paymentType: "INSTANT",
  },
];

function generateTransactionId() {
  let transactionId = "EA";
  for (let i = 0; i < 14; i++) {
    transactionId += Math.floor(Math.random() * 10);
  }
  return transactionId;
}

const VerifyPage = () => {
  const { type } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const config = verificationConfig[type];
  const sampleType = SAMPLE_POPUP_TYPE_MAP[type] || "nin";
  const sampleMeta = sampleData[sampleType] || sampleData.nin;
  const user = useSelector((state) => state.user);
  const userToken = user?.jwtToken || "";
  const userDetails = useSelector((state) => state.userDetails);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [selectedPaymentMethod, setPaymentMethod] = useState("wallet");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pricingData, setPricingData] = useState(null);
  const [allServicePrices, setAllServicePrices] = useState(null);
  const [detectedCurrency, setDetectedCurrency] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [priceError, setPriceError] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [selectedBureaus, setSelectedBureaus] = useState({});
  const [paystackModalOpen, setPaystackModalOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paystackReference, setPaystackReference] = useState("");
  const [activeGateway, setActiveGateway] = useState(""); // "paystack" or "flutterwave"
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [consentPending, setConsentPending] = useState(false);
  const [consentRequestId, setConsentRequestId] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(true);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const pollingRef = useRef(null);
  const pendingApiFormRef = useRef(null);
  const consentPollingRef = useRef(null);
  const paymentAttemptInFlightRef = useRef(false);
  const funnelTrackedRef = useRef({
    formSubmit: false,
    beginCheckout: false,
    verificationStarted: false,
  });

  // Dynamic steps based on whether this verification type requires consent
  const isConsentVerification =
    type === "nin" ||
    type === "phone" ||
    type === "bvn" ||
    Boolean(config?.requiresConsent);
  const requiresConsent = isConsentVerification;
  const STEPS = requiresConsent
    ? ["Search", "Payment", "Processing", "Consent", "Result"]
    : ["Search", "Payment", "Processing", "Result"];
  const RESULT_STEP = requiresConsent ? 4 : 3;
  const CONSENT_STEP = 3;

  // Redirect if invalid type
  useEffect(() => {
    if (!config) {
      history.replace("/main-dashboard");
    }
  }, [config, history]);

  // Poll payment status for Paystack or Flutterwave
  useEffect(() => {
    if (!paystackModalOpen || !paystackReference) return;

    let isHandled = false;

    const checkEndpoint =
      activeGateway === "flutterwave"
        ? `${baseUrl}/payment/check?transactionRef=${paystackReference}`
        : `${baseUrl}/payment/check-pulse?transactionRef=${paystackReference}`;

    pollingRef.current = setInterval(async () => {
      if (isHandled) return;
      try {
        const res = await fetch(checkEndpoint, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          const paymentStatus = (
            data?.data?.status ||
            data?.data?.paymentStatus ||
            ""
          ).toLowerCase();

          if (paymentStatus === "successful" || paymentStatus === "success") {
            isHandled = true;
            if (pollingRef.current) clearInterval(pollingRef.current);
            setPaystackModalOpen(false);
            setPaymentUrl("");
            trackPurchase(type, {
              gateway: activeGateway,
              transaction_id: paystackReference,
              value: totalAmount,
              currency: currencyCheck,
            });
            setPaystackReference("");
            setLoading(true);
            setCurrentStep(2); // Processing
            if (pendingApiFormRef.current) {
              await handleCompleteVerification(pendingApiFormRef.current);
            }
          } else if (
            ["failed", "cancelled", "error", "reversed"].includes(paymentStatus)
          ) {
            isHandled = true;
            if (pollingRef.current) clearInterval(pollingRef.current);
            setPaystackModalOpen(false);
            setPaymentUrl("");
            setPaystackReference("");
            Swal.fire({
              icon: "error",
              title: "Payment Failed",
              text:
                data?.data?.processor_response ||
                "Your payment could not be completed. Please try again.",
              confirmButtonColor: "#09c93a",
            });
          }
          // Note: "abandoned", "pending", "ongoing" means the user is still in the checkout modal. Keep polling!
        }
      } catch {
        // Ignore polling errors
      }
    }, 4000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [paystackModalOpen, paystackReference, userToken, activeGateway]);

  // Consent polling
  useEffect(() => {
    if (!consentPending || !consentRequestId) return;

    const checkConsent = async () => {
      try {
        const response = await apiGet(
          `/verification/check-consent/${consentRequestId}`,
          userToken,
        );
        if (response?.consent === "granted") {
          setConsentPending(false);
          if (consentPollingRef.current)
            clearInterval(consentPollingRef.current);
          Swal.fire({
            icon: "success",
            title: "Verification Successful",
            text: "Consent has been granted and your verification is complete.",
            confirmButtonText: "OK",
            confirmButtonColor: "#09c93a",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            history.push("/main-dashboard");
          });
        }
      } catch (err) {
        console.error("Consent check error:", err);
      }
    };

    // Check immediately, then poll every 15 seconds
    checkConsent();
    consentPollingRef.current = setInterval(checkConsent, 15000);

    return () => {
      if (consentPollingRef.current) clearInterval(consentPollingRef.current);
    };
  }, [consentPending, consentRequestId, userToken]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (consentPollingRef.current) clearInterval(consentPollingRef.current);
    };
  }, []);

  // Fetch service prices
  const fetchPrices = useCallback(async () => {
    if (!config || !userToken) return;

    setLoadingPrice(true);
    setPriceError(null);
    try {
      const ipAddress = localStorage.getItem("IpAddress");
      const response = await apiPostInternalCall(
        `/transaction/service-prices`,
        { ipAddress, pricingSegment: getPricingSegment() },
        userToken,
      );
      const allData = response?.data?.data;
      savePricingSegmentFromCurrency(allData?.[0]?.currency);
      setAllServicePrices({ data: allData, rate: response?.data?.rate });

      const targetIndex =
        type === "vehicle" && formData.platform === "premium"
          ? 7
          : config.priceIndex;
      const serviceData = allData?.[targetIndex];
      if (!serviceData) {
        throw new Error("Service price information is currently unavailable.");
      }
      setDetectedCurrency(allData?.[0]?.currency || null);
      setPricingData({
        price: serviceData.price,
        serviceFee: serviceData.serviceFee,
        vat: serviceData.VAT,
        priceUsd: serviceData.price2,
        serviceFeeusd: serviceData.serviceFee2,
        vatUsd: serviceData.VAT2,
        processingFee: serviceData.processingFee || 0,
        rate: response?.data?.rate,
      });
      setLoadingPrice(false);
    } catch (err) {
      setLoadingPrice(false);
      setPricingData(null);
      setPriceError("Could not fetch service prices. Please try again.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not fetch service prices. Please try again.",
        confirmButtonColor: "#09c93a",
      });
    }
  }, [config, userToken]); // intentionally removed formData.vehiclePackage to avoid re-fetching

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  useEffect(() => {
    if (allServicePrices && config) {
      const targetIndex =
        type === "vehicle" && formData.platform === "premium"
          ? 7
          : config.priceIndex;
      const serviceData = allServicePrices.data?.[targetIndex];
      if (serviceData) {
        setPricingData({
          price: serviceData.price,
          serviceFee: serviceData.serviceFee,
          vat: serviceData.VAT,
          priceUsd: serviceData.price2,
          serviceFeeusd: serviceData.serviceFee2,
          vatUsd: serviceData.VAT2,
          processingFee: serviceData.processingFee || 0,
          rate: allServicePrices.rate,
        });
      }
    }
  }, [allServicePrices, config, type, formData.platform]);

  if (!config) return null;

  const baseCurrency = (
    detectedCurrency ||
    localStorage.getItem("currencyCheck") ||
    "NGN"
  ).toUpperCase();
  const isBaseNGN = baseCurrency === "NGN";
  // For users outside Nigeria the API returns the Naira equivalent in price2/VAT2
  const nairaTotal = Number(pricingData?.priceUsd) || 0;
  const canPayInNaira = !isBaseNGN && nairaTotal > 0;
  const currencyCheck =
    canPayInNaira && selectedCurrency === "NGN" ? "NGN" : baseCurrency;
  const isNGN = currencyCheck === "NGN";
  const useNairaEquivalent = isNGN && !isBaseNGN;

  const bureauCount = config?.bureaus
    ? Object.values(selectedBureaus).filter(Boolean).length
    : 0;
  const allBureausSelected =
    config?.bureaus && bureauCount === config.bureaus.length;
  const bureauMultiplier = config?.bureaus ? Math.max(bureauCount, 1) : 1;
  const discount =
    allBureausSelected && config?.allBureausDiscount
      ? isNGN
        ? config.allBureausDiscount.ngn
        : config.allBureausDiscount.usd
      : 0;

  const unitTax = pricingData
    ? (useNairaEquivalent ? pricingData.vatUsd : pricingData.vat) || 0
    : 0;
  const unitProcessing = pricingData
    ? useNairaEquivalent
      ? Math.max(nairaTotal - unitTax, 0)
      : (pricingData.serviceFee || 0) + (pricingData.processingFee || 0)
    : 0;

  const totalAmount = pricingData
    ? (unitProcessing + unitTax) * bureauMultiplier - discount
    : 0;

  const currencySymbol = isNGN ? "₦" : "$";
  const walletCurrency = (userDetails?.currency || baseCurrency).toUpperCase();
  const walletSymbol = walletCurrency === "NGN" ? "₦" : "$";
  // Cross-currency wallet payments are converted and validated by the backend
  const paymentMethod = selectedPaymentMethod;

  const userInitials = userDetails
    ? `${(userDetails.firstName || "")[0] || ""}${
        (userDetails.lastName || "")[0] || ""
      }`.toUpperCase()
    : "U";

  const userName = userDetails
    ? `${userDetails.firstName || ""} ${userDetails.lastName || ""}`.trim()
    : "User";

  const userEmail = userDetails?.email || "";
  const userBalance = userDetails?.walletBalance || 0;

  const resetFunnelTracking = () => {
    funnelTrackedRef.current = {
      formSubmit: false,
      beginCheckout: false,
      verificationStarted: false,
    };
  };

  const trackFormSubmitOnce = () => {
    if (funnelTrackedRef.current.formSubmit) return;
    funnelTrackedRef.current.formSubmit = true;
    trackFormSubmit(type);
  };

  const trackBeginCheckoutOnce = (params = {}) => {
    if (funnelTrackedRef.current.beginCheckout) return;
    funnelTrackedRef.current.beginCheckout = true;
    trackBeginCheckout(type, params);
  };

  const trackVerificationStartedOnce = () => {
    if (funnelTrackedRef.current.verificationStarted) return;
    funnelTrackedRef.current.verificationStarted = true;
    trackVerificationStarted(type);
  };

  /* ── Form handlers ── */

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For eitherOr fields, clear the sibling field
    const currentField = config.fields.find((f) => f.name === name);
    const updates = { [name]: value };
    if (currentField?.eitherOr) {
      config.fields.forEach((f) => {
        if (f.eitherOr === currentField.eitherOr && f.name !== name) {
          updates[f.name] = "";
        }
      });
    }

    setFormData((prev) => ({ ...prev, ...updates }));
    if (currentStep === 0) {
      resetFunnelTracking();
    }
    setError("");
  };

  const handleClear = () => {
    setFormData({});
    setSelectedBureaus({});
    resetFunnelTracking();
    setError("");
  };

  const handleSelectAllBureaus = () => {
    if (!config.bureaus) return;
    const allSelected = config.bureaus.every((b) => selectedBureaus[b.id]);
    setSelectedBureaus(
      allSelected
        ? {}
        : config.bureaus.reduce((acc, b) => ({ ...acc, [b.id]: true }), {}),
    );
    setError("");
  };

  const isFormValid = () => {
    // Check all strictly required fields
    const requiredValid = config.fields
      .filter((f) => f.required)
      .every((f) => formData[f.name]?.trim());

    // Check either/or groups (at least one must be filled)
    const eitherOrGroups = {};
    config.fields.forEach((f) => {
      if (f.eitherOr) {
        if (!eitherOrGroups[f.eitherOr]) eitherOrGroups[f.eitherOr] = [];
        eitherOrGroups[f.eitherOr].push(f.name);
      }
    });
    const eitherOrValid = Object.values(eitherOrGroups).every((group) =>
      group.some((name) => formData[name]?.trim()),
    );

    // Check bureau selection (at least one required if config has bureaus)
    const bureauValid = config.bureaus
      ? Object.values(selectedBureaus).some(Boolean)
      : true;

    return requiredValid && eitherOrValid && bureauValid;
  };

  /* ── Step navigation ── */

  const handleContinueToPayment = () => {
    if (loadingPrice) {
      setError("Please wait while verification pricing is being loaded.");
      return;
    }
    if (priceError || !pricingData) {
      setError(
        "Unable to proceed: service prices could not be loaded. Please check your connection and try again.",
      );
      Swal.fire({
        icon: "warning",
        title: "Pricing Unavailable",
        text: "Could not retrieve service price. Please try again.",
        confirmButtonColor: "#09c93a",
      });
      return;
    }
    if (!isFormValid()) {
      const hasEitherOr = config.fields.some((f) => f.eitherOr);
      const noBureauSelected =
        config.bureaus && !Object.values(selectedBureaus).some(Boolean);
      setError(
        noBureauSelected
          ? "Please select at least one credit bureau."
          : hasEitherOr
            ? "Please fill in at least one of the fields."
            : "Please fill in all required fields.",
      );
      return;
    }
    setError("");
    trackFormSubmitOnce();
    setShowDisclaimer(true);
  };

  const handleDisclaimerConfirm = () => {
    if (priceError || !pricingData) {
      setShowDisclaimer(false);
      setError(
        "Unable to proceed: service prices could not be loaded. Please try again.",
      );
      return;
    }
    setShowDisclaimer(false);
    trackBeginCheckoutOnce({
      amount: totalAmount,
      value: totalAmount,
      currency: currencyCheck,
    });
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToSearch = () => {
    setCurrentStep(0);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Build formData for API ── */

  const buildApiFormData = () => {
    const apiForm = {
      nin: "",
      phone: "",
      firstname: "",
      lastname: "",
      dateOfBirth: "",
      gender: "",
      rc: "",
      business_name: "",
      bvn: "",
      vin: "",
      stolencheck:
        type === "vehicle" && formData.platform === "premium" ? true : "",
      license_number: "",
      face: "",
      nin_csv: "",
      crc: "",
      firstCentral: "",
      creditRegistry: "",
      paymentType: "",
      currency: "",
      platform: type === "vehicle" ? formData.platform || "standard" : "",
    };

    // Map form fields to API form
    config.fields.forEach((field) => {
      if (field.name !== "purpose" && formData[field.name]) {
        apiForm[field.name] = formData[field.name];
      }
    });

    // Map selected bureaus to API form
    if (config.bureaus) {
      config.bureaus.forEach((bureau) => {
        apiForm[bureau.fieldName] = selectedBureaus[bureau.id] ? true : "";
      });
    }

    return apiForm;
  };

  /* ── Payment flow ── */

  const handlePay = async () => {
    if (priceError || !pricingData) {
      setError(
        "Unable to proceed: service prices could not be loaded. Please try again.",
      );
      return;
    }
    if (paymentAttemptInFlightRef.current) return;
    paymentAttemptInFlightRef.current = true;
    setLoading(true);
    setError("");
    setCurrentStep(2); // Processing
    window.scrollTo({ top: 0, behavior: "smooth" });

    const randomTransactionId = generateTransactionId();
    const selectedMethod = PAYMENT_METHODS.find((m) => m.id === paymentMethod);

    localStorage.setItem("transactionID", randomTransactionId);
    localStorage.setItem("paymentType", selectedMethod.paymentType);
    localStorage.setItem("totalAmount", totalAmount);
    // Verification requests (initiate/complete) read the currency from here
    localStorage.setItem("currencyCheck", currencyCheck);

    const apiFormData = buildApiFormData();

    try {
      // 1. Initiate verification
      trackVerificationStartedOnce();
      const initiateResponse = await dispatch(
        initiateVerificationRequest(apiFormData, userToken),
      );

      if (initiateResponse?.sessionStatus === "INITIATED") {
        localStorage.setItem("sessionCode", initiateResponse?.sessionCode);
      } else {
        throw new Error(
          initiateResponse?.message || "Failed to initiate verification",
        );
      }

      // 2. Process payment based on method
      if (paymentMethod === "wallet") {
        await handleWalletPayment(randomTransactionId, apiFormData);
      } else if (paymentMethod === "flutterwave") {
        await handleFlutterwavePayment(randomTransactionId, apiFormData);
      } else {
        await handlePaystackPayment(randomTransactionId, apiFormData);
      }
    } catch (err) {
      setLoading(false);
      setCurrentStep(1);
      setError(err.message || "An error occurred. Please try again.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An error occurred. Please try again.",
        confirmButtonColor: "#09c93a",
      }).then(() => {
        history.push("/main-dashboard");
      });
    } finally {
      paymentAttemptInFlightRef.current = false;
    }
  };

  const handleWalletPayment = async (transactionId, apiFormData) => {
    // Check wallet balance (only comparable when currencies match)
    if (walletCurrency === currencyCheck && userBalance < totalAmount) {
      setLoading(false);
      setCurrentStep(1);
      Swal.fire({
        icon: "error",
        title: "Wallet Balance Low",
        text: `Your wallet balance (${walletSymbol}${userBalance.toLocaleString()}) is insufficient for this transaction (${currencySymbol}${totalAmount.toLocaleString()}). Please select a different payment method.`,
        confirmButtonColor: "#09c93a",
      });
      return;
    }

    try {
      const apiUrl = `${
        require("../../apiConfig").default
      }/transaction/wallet-payment`;
      const requestBody = {
        sessionCode: localStorage.getItem("sessionCode"),
        userNIN: userDetails?.nin || "",
        transactionID: transactionId,
        currency: currencyCheck,
        paymentType: "WALLET",
        amount: totalAmount,
      };

      trackPaymentInitiated(type, {
        amount: totalAmount,
        value: totalAmount,
        currency: currencyCheck,
        gateway: "Wallet",
        transaction_id: transactionId,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(await withAnalyticsMetadata(requestBody)),
      });

      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        data = {
          message: "Unable to process wallet payment. Please try again.",
        };
      }

      if (response.ok && data.status === "success") {
        trackPurchase(type, {
          gateway: "Wallet",
          transaction_id: transactionId,
          value: totalAmount,
          currency: currencyCheck,
        });
        await handleCompleteVerification(apiFormData);
      } else {
        throw new Error(data.message || "Wallet payment failed");
      }
    } catch (err) {
      throw err;
    }
  };

  const handlePaystackPayment = async (transactionId, apiFormData) => {
    try {
      const paymentData = {
        amount: totalAmount,
        currency: currencyCheck,
        type: "VERIFICATION",
        sessionCode: localStorage.getItem("sessionCode"),
      };

      const response = await initiatePaystackPayment(paymentData, userToken);

      if (response?.data?.authorization_url) {
        pendingApiFormRef.current = apiFormData;
        setPaymentUrl(response.data.authorization_url);
        setPaystackReference(response.data.reference);
        setActiveGateway("paystack");
        trackPaymentInitiated(type, {
          amount: totalAmount,
          value: totalAmount,
          currency: currencyCheck,
          gateway: "Paystack",
          transaction_id: response.data.reference,
        });
        setPaystackModalOpen(true);
        setLoading(false);
        setCurrentStep(1); // Stay on payment step while modal is open
      } else {
        throw new Error("Failed to initialize payment gateway");
      }
    } catch (err) {
      throw err;
    }
  };

  const handleFlutterwavePayment = async (transactionId, apiFormData) => {
    try {
      const postData = {
        amount: totalAmount,
        currency: currencyCheck,
        country: "NG",
        description: "Payment for verification",
        payment_method: "card,mobilemoney,ussd",
        type: "VERIFICATION",
        sessionCode: localStorage.getItem("sessionCode"),
      };

      const response = await fetch(`${baseUrl}/payment/flexi-initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(await withAnalyticsMetadata(postData)),
      });

      const responseData = await response.json();

      if (responseData?.data?.link) {
        pendingApiFormRef.current = apiFormData;
        setPaymentUrl(responseData.data.link);
        setPaystackReference(responseData.data.txRef || transactionId);
        localStorage.setItem(
          "transactionID",
          responseData.data.txRef || transactionId,
        );
        trackPaymentInitiated(type, {
          amount: totalAmount,
          value: totalAmount,
          currency: currencyCheck,
          gateway: "Flutterwave",
          transaction_id: responseData.data.txRef || transactionId,
        });
        setActiveGateway("flutterwave");
        setPaystackModalOpen(true);
        setLoading(false);
        setCurrentStep(1);
      } else {
        throw new Error("Failed to initialize Flutterwave payment");
      }
    } catch (err) {
      throw err;
    }
  };

  const handlePaystackModalClose = async () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setPaystackModalOpen(false);
    setPaymentUrl("");

    const ref = paystackReference;
    setPaystackReference("");
    if (!ref) return;

    const checkEndpoint =
      activeGateway === "flutterwave"
        ? `${baseUrl}/payment/check?transactionRef=${ref}`
        : `${baseUrl}/payment/check-pulse?transactionRef=${ref}`;

    try {
      const res = await fetch(checkEndpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      });

      if (!res.ok) {
        Swal.fire({
          icon: "error",
          title: "Payment Cancelled",
          text: "Your payment was cancelled or declined. Please try again.",
          didOpen: () =>
            trackPaymentFailed(type, {
              gateway: activeGateway,
              value: totalAmount,
              currency: currencyCheck,
            }),
          confirmButtonColor: "#09c93a",
        });
        return;
      }

      const data = await res.json();
      const paymentStatus = (
        data?.data?.status ||
        data?.status ||
        data?.data?.paymentStatus ||
        ""
      ).toLowerCase();

      if (paymentStatus === "successful" || paymentStatus === "success") {
        // Payment succeeded before closing — proceed with verification
        trackPurchase(type, {
          gateway: activeGateway,
          transaction_id: paystackReference,
          value: totalAmount,
          currency: currencyCheck,
        });
        setLoading(true);
        setCurrentStep(2); // Processing
        if (pendingApiFormRef.current) {
          await handleCompleteVerification(pendingApiFormRef.current);
        }
        return;
      } else {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: "Your payment could not be completed. Please try again.",
          didOpen: () =>
            trackPaymentFailed(type, {
              gateway: activeGateway,
              value: totalAmount,
              currency: currencyCheck,
            }),
          confirmButtonColor: "#09c93a",
        });
      }
    } catch {
      Swal.fire({
        icon: "info",
        title: "Payment Cancelled",
        text: "Payment window was closed. Please try again.",
        confirmButtonColor: "#09c93a",
      });
    }
  };

  const handleCompleteVerification = async (apiFormData) => {
    try {
      const response = await dispatch(
        completeVerificationRequest(apiFormData, userToken),
      );

      dispatch(fetchUserProfile(userToken));
      setLoading(false);

      // Determine result and result page route
      let result = null;
      let resultTitle = "Verification Successful";
      let resultDetail = "";
      let resultRoute = "/main-dashboard";

      if (
        response.basic &&
        response.basic.status &&
        response.basic.status === true
      ) {
        result =
          response.basic?.nin_data || response.basic?.data || response.basic;
        resultDetail =
          response.basic.detail || "Your NIN verification was successful.";
        resultRoute = "/main-dashboard";
      } else if (
        response.basic &&
        typeof response.basic.status === "boolean" &&
        response.basic.status === false
      ) {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: response.basic.detail,
          confirmButtonColor: "#09c93a",
        }).then(() => {
          history.push("/main-dashboard");
        });
        setCurrentStep(1);
        return;
      } else if (
        response["search-extension"] &&
        response["search-extension"].phoneVerification &&
        response["search-extension"].phoneVerification.status === true
      ) {
        const phoneVerification =
          response["search-extension"].phoneVerification;
        result = phoneVerification.data || phoneVerification;
        resultDetail =
          phoneVerification.detail || "Phone verification was successful.";
        resultRoute = "/main-dashboard";
      } else if (
        response["search-extension"] &&
        response["search-extension"].phoneVerification &&
        response["search-extension"].phoneVerification.status === false
      ) {
        const phoneVerificationDetail =
          response["search-extension"].phoneVerification.detail || "";
        const recordNotFound = isRecordNotFoundDetail(phoneVerificationDetail);
        Swal.fire({
          icon: "error",
          title: recordNotFound
            ? RECORD_NOT_FOUND_TITLE
            : "Verification Failed",
          text: recordNotFound
            ? RECORD_NOT_FOUND_MESSAGE
            : phoneVerificationDetail || "Verification failed",
          confirmButtonColor: "#09c93a",
        }).then(() => {
          history.push("/main-dashboard");
        });
        setCurrentStep(1);
        return;
      } else if (
        response["search-extension"] &&
        response["search-extension"].bvnVerification &&
        response["search-extension"].bvnVerification.status === true
      ) {
        const bvnVerification = response["search-extension"].bvnVerification;
        result = bvnVerification.data || bvnVerification;
        resultDetail =
          bvnVerification.detail || "BVN verification was successful.";
        resultRoute = "/main-dashboard";
      } else if (
        response.business &&
        response.business.success === true &&
        response.business.data &&
        response.business.data !== "null"
      ) {
        const bizArray = Array.isArray(response.business.data)
          ? response.business.data.map((item) => item.data || item)
          : [response.business.data];
        const firstBiz = bizArray[0];
        result = {
          _isBusinessList: true,
          businesses: bizArray,
          ...(firstBiz || {}),
        };
        resultTitle = "Business Verification Successful";
        resultDetail =
          bizArray.length > 1
            ? `${bizArray.length} companies found matching your search.`
            : firstBiz?.approvedName ||
              "Business has been verified successfully.";
        resultRoute = "/main-dashboard";
      } else if (
        response.business &&
        (response.business.success === false || response.business.error)
      ) {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text:
            response.business.error?.message ||
            response.business.message ||
            "Verification failed. Please try again.",
          confirmButtonColor: "#09c93a",
        }).then(() => {
          history.push("/main-dashboard");
        });
        setCurrentStep(1);
        return;
      } else if (response.financial && response.financial.success === true) {
        result = response.financial;
        resultDetail =
          response.financial.message ||
          "Financial verification was successful.";
        resultRoute = "/main-dashboard";
      } else if (response.financial && response.financial.success === false) {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: response.financial.message,
          confirmButtonColor: "#09c93a",
        }).then(() => {
          history.push("/main-dashboard");
        });
        setCurrentStep(1);
        return;
      } else if (response.vehicle && response.vehicle.success === true) {
        const vehicleData = response.vehicle.data || response.vehicle;
        result = vehicleData;
        resultTitle = "Vehicle Verification Successful";
        resultDetail =
          vehicleData.vehicleName || "VIN verification was successful.";
        resultRoute = "/vehicle-profile-result";
      } else if (
        response?.advance ||
        response?.firstCentral ||
        response?.crc ||
        response?.creditRegistry
      ) {
        // Credit bureau — check which bureaus returned data
        const bureauResults = [];
        const bureauErrors = [];
        if (response?.crc) {
          if (response.crc.data && response.crc.data !== "null")
            bureauResults.push("CRC");
          else if (response.crc.error)
            bureauErrors.push(
              "CRC: " + (response.crc.error.message || "Failed"),
            );
        }
        if (response?.firstCentral) {
          if (
            response.firstCentral.data &&
            response.firstCentral.data !== "null"
          )
            bureauResults.push("First Central");
          else if (response.firstCentral.error)
            bureauErrors.push(
              "First Central: " +
                (response.firstCentral.error.message || "Failed"),
            );
        }
        if (response?.creditRegistry) {
          if (
            response.creditRegistry.data &&
            response.creditRegistry.data !== "null"
          )
            bureauResults.push("Credit Registry");
          else if (response.creditRegistry.error)
            bureauErrors.push(
              "Credit Registry: " +
                (response.creditRegistry.error.message || "Failed"),
            );
        }
        if (response?.advance) {
          if (response.advance.data && response.advance.data !== "null")
            bureauResults.push("Advance");
          else if (response.advance.error)
            bureauErrors.push(
              "Advance: " + (response.advance.error.message || "Failed"),
            );
        }

        const hasAnyData = bureauResults.length > 0;

        if (hasAnyData) {
          result = {
            bureauResults,
            bureauErrors,
            advance: response?.advance,
            firstCentral: response?.firstCentral,
            crc: response?.crc,
            creditRegistry: response?.creditRegistry,
          };
          resultTitle =
            bureauErrors.length > 0
              ? "Partial Results Available"
              : "Credit Profile Results";
          resultDetail = `Data received from: ${bureauResults.join(", ")}.`;
          resultRoute = "/financial-profile-result";
        } else {
          Swal.fire({
            icon: "error",
            title: "Verification Failed",
            text:
              bureauErrors.length > 0
                ? bureauErrors.join("\n")
                : "Verification failed. Please try again.",
            confirmButtonColor: "#09c93a",
          }).then(() => {
            history.push("/main-dashboard");
          });
          setCurrentStep(1);
          return;
        }
      }

      if (result) {
        setVerificationResult({
          data: result,
          title: resultTitle,
          detail: resultDetail,
          route: resultRoute,
        });

        if (isConsentVerification) {
          // Extract requestId from API response if present
          const requestId =
            response.basic?.data?.requestId ||
            response.basic?.requestId ||
            response["search-extension"]?.phoneVerification?.requestId ||
            response["search-extension"]?.bvnVerification?.requestId ||
            response.financial?.requestId ||
            "";

          if (requestId) {
            setConsentRequestId(requestId);
            localStorage.setItem("verificationRequestId", requestId);
          }

          const isPendingConsent =
            Boolean(requestId) &&
            (response.basic?.consent === "pending" ||
              response["search-extension"]?.phoneVerification?.consent ===
                "pending" ||
              response["search-extension"]?.bvnVerification?.consent ===
                "pending" ||
              response.financial?.consent === "pending" ||
              response.consent === "pending");

          if (isPendingConsent) {
            setConsentPending(true);
            setCurrentStep(CONSENT_STEP);
          } else {
            // Take away result popup for consent verifications; show standard success alert
            Swal.fire({
              icon: "success",
              title: "Verification Successful",
              text: resultDetail || "Your verification was successful.",
              confirmButtonText: "OK",
              confirmButtonColor: "#09c93a",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then(() => {
              history.push("/main-dashboard");
            });
          }
        } else {
          setShowResultPopup(true);
          setCurrentStep(2); // Keep processing visible while popup appears
        }
      } else {
        // Verification failed — generic fallback
        // Also check response?.data?.message (Axios error.response shape) and
        // response?.message (plain API error body) to surface server error messages
        // like "No matching payload keys or sessionType found to validate for this transaction"
        const errorMsg =
          response?.basic?.detail ||
          response?.["search-extension"]?.phoneVerification?.detail ||
          response?.["search-extension"]?.bvnVerification?.detail ||
          response?.business?.error?.message ||
          response?.business?.message ||
          response?.financial?.error?.message ||
          response?.financial?.message ||
          response?.vehicle?.error?.message ||
          response?.vehicle?.message ||
          response?.data?.message ||
          response?.message ||
          "Verification could not be completed. Service is not available at the moment.";
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: errorMsg,
          confirmButtonColor: "#09c93a",
        }).then(() => {
          history.push("/main-dashboard");
        });
        setCurrentStep(1);
      }
    } catch (err) {
      setLoading(false);
      setCurrentStep(1);
      const catchMsg =
        err?.response?.data?.message ||
        err?.message ||
        "The service is not available at the moment.";
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: catchMsg,
        confirmButtonColor: "#09c93a",
      }).then(() => {
        history.push("/main-dashboard");
      });
    }
  };

  /* ── Render helpers ── */

  const renderStepper = () => (
    <StepperWrapper>
      <StepperInner>
        {STEPS.map((step, i) => (
          <React.Fragment key={step}>
            <StepItem isLast={i === STEPS.length - 1}>
              <StepCircle
                active={currentStep === i}
                completed={currentStep > i}
              >
                {currentStep > i ? (
                  <FaCheckCircle style={{ fontSize: 14 }} />
                ) : (
                  i + 1
                )}
              </StepCircle>
              <StepLabel active={currentStep === i}>{step}</StepLabel>
            </StepItem>
            {i < STEPS.length - 1 && <StepLine completed={currentStep > i} />}
          </React.Fragment>
        ))}
      </StepperInner>
    </StepperWrapper>
  );

  const renderSearchStep = () => (
    <FormCard>
      <FormCardTitle>Enter search details</FormCardTitle>
      <FormCardSub>
        Provide the details of the individual you want to verify.
      </FormCardSub>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      <SearchGrid>
        <div>
          <FormGroup>
            <FormLabel>ID Type</FormLabel>
            <IdTypeDisplay>
              <FaIdCard />
              {config.idTypeLabel}
            </IdTypeDisplay>
          </FormGroup>
          {config.fields.map((field, idx) => (
            <React.Fragment key={field.name}>
              {field.eitherOr &&
                idx > 0 &&
                config.fields[idx - 1]?.eitherOr === field.eitherOr && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      margin: "4px 0 12px",
                    }}
                  >
                    <div
                      style={{ flex: 1, height: 1, background: "#e5e7eb" }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#999",
                        fontFamily: "Nunito, sans-serif",
                      }}
                    >
                      OR
                    </span>
                    <div
                      style={{ flex: 1, height: 1, background: "#e5e7eb" }}
                    />
                  </div>
                )}
              <FormGroup>
                <FormLabel>
                  {field.label}
                  {field.required && (
                    <span style={{ color: "#dc2626" }}> *</span>
                  )}
                </FormLabel>
                {field.type === "select" ? (
                  <FormSelect
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">{field.placeholder}</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </FormSelect>
                ) : (
                  <FormInput
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ""}
                    onChange={handleInputChange}
                    maxLength={field.maxLength}
                  />
                )}
                {field.showCounter && (
                  <CharCounter>
                    {(formData[field.name] || "").length}/{field.maxLength}
                  </CharCounter>
                )}
              </FormGroup>
            </React.Fragment>
          ))}
          {/* TODO: Uncomment this section if platform selection is needed for */}
          {/* {type === "vehicle" && (
            <FormGroup>
              <FormLabel>Select Platform</FormLabel>
              <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      formData.platform === "standard" || !formData.platform
                    }
                    onChange={() =>
                      handleInputChange({
                        target: { name: "platform", value: "standard" },
                      })
                    }
                    style={{
                      cursor: "pointer",
                      width: "18px",
                      height: "18px",
                      accentColor: "#09c93a",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "15px",
                      fontFamily: "Nunito, sans-serif",
                      color: "#374151",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    Standard
                    <Tooltip
                      title={
                        <div>
                          <strong>Standard VIN Search</strong>
                          <br />
                          Essential vehicle information: Vehicle identity,
                          specifications, engine, transmission, dimensions, fuel
                          information, and other available vehicle details.
                        </div>
                      }
                    >
                      <FaInfoCircle color="#999" />
                    </Tooltip>
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.platform === "premium"}
                    onChange={() =>
                      handleInputChange({
                        target: { name: "platform", value: "premium" },
                      })
                    }
                    style={{
                      cursor: "pointer",
                      width: "18px",
                      height: "18px",
                      accentColor: "#09c93a",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "15px",
                      fontFamily: "Nunito, sans-serif",
                      color: "#374151",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    Premium
                    <Tooltip
                      title={
                        <div>
                          <strong>Advanced VIN Search</strong>
                          <br />
                          Detailed vehicle history & risk report: Everything you
                          need to investigate a used vehicle's available history
                          — including accident, title, salvage, mileage,
                          insurance, lien, auction, valuation, theft, and recall
                          records.
                        </div>
                      }
                    >
                      <FaInfoCircle color="#999" />
                    </Tooltip>
                  </span>
                </label>
              </div>
            </FormGroup>
          )} */}
          {config.bureaus && (
            <FormGroup>
              <FormLabel
                style={{
                  marginBottom: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>
                  Select Credit Bureau(s){" "}
                  <span style={{ color: "#dc2626" }}> *</span>
                </span>
                <Tooltip
                  title="Your credit information may vary across credit bureaus. For a more comprehensive view of your credit history, we recommend checking your reports from all three licensed credit bureaus in Nigeria."
                  placement="top"
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      color: "#09c93a",
                      fontSize: 15,
                    }}
                    aria-label="Credit bureau information"
                  >
                    <FaInfoCircle />
                  </span>
                </Tooltip>
              </FormLabel>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "10px 14px",
                  marginBottom: 10,
                  borderRadius: 10,
                  background: allBureausSelected
                    ? "#f0fdf4"
                    : "var(--ec-bg-secondary, #f9fafb)",
                  border: `1.5px solid ${allBureausSelected ? "#09c93a" : "#d1fae5"}`,
                }}
              >
                <button
                  type="button"
                  onClick={handleSelectAllBureaus}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 8,
                    border: `1.5px solid ${allBureausSelected ? "#068a28" : "#09c93a"}`,
                    background: allBureausSelected ? "#fff" : "#09c93a",
                    color: allBureausSelected ? "#068a28" : "#fff",
                    fontFamily: "Nunito, sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: allBureausSelected
                      ? "none"
                      : "0 2px 8px rgba(9, 201, 58, 0.25)",
                  }}
                >
                  <FaCheckCircle style={{ fontSize: 14 }} />
                  {allBureausSelected ? "Deselect All" : "Select All"}
                </button>
                {config.allBureausDiscount && (
                  <span
                    style={{
                      fontSize: 13,
                      color: "#16a34a",
                      fontFamily: "Nunito, sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {allBureausSelected ? (
                      <>
                        🎉 All 3 Bureaus Discount Applied: -{currencySymbol}
                        {(isNGN
                          ? config.allBureausDiscount.ngn
                          : config.allBureausDiscount.usd
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </>
                    ) : (
                      <>
                        Select all 3 bureaus to save {currencySymbol}
                        {(isNGN
                          ? config.allBureausDiscount.ngn
                          : config.allBureausDiscount.usd
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </>
                    )}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {config.bureaus.map((bureau) => (
                  <label
                    key={bureau.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      border: `1.5px solid ${selectedBureaus[bureau.id] ? "#09c93a" : "#e5e7eb"}`,
                      borderRadius: 8,
                      cursor: "pointer",
                      background: selectedBureaus[bureau.id]
                        ? "#f0fdf4"
                        : "#fff",
                      transition: "all 0.15s",
                      fontFamily: "Nunito, sans-serif",
                      fontSize: 14,
                      color: "#333",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedBureaus[bureau.id]}
                      onChange={(e) => {
                        setSelectedBureaus((prev) => ({
                          ...prev,
                          [bureau.id]: e.target.checked,
                        }));
                        setError("");
                      }}
                      style={{ accentColor: "#09c93a", width: 16, height: 16 }}
                    />
                    {bureau.label}
                  </label>
                ))}
              </div>
            </FormGroup>
          )}
          <YouWillGetCard>
            <YouWillGetTitle>You will get</YouWillGetTitle>
            <YouWillGetRow>
              {config.youWillGet.map((item, i) => (
                <YouWillGetItem key={i}>
                  <item.icon />
                  {item.text}
                </YouWillGetItem>
              ))}
            </YouWillGetRow>
          </YouWillGetCard>
          <SidebarNote>
            <FaShieldAlt /> Your data is secure and used only for verification.
          </SidebarNote>
        </div>

        <SidebarCard>
          {renderCurrencySelector()}
          <PriceLabel>Amount</PriceLabel>
          <PriceAmount>
            {loadingPrice
              ? "Loading..."
              : priceError || !pricingData
                ? "-"
                : `${currencySymbol}${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          </PriceAmount>
          {(priceError || (!loadingPrice && !pricingData)) && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "10px 12px",
                color: "#b91c1c",
                fontSize: 13,
                marginBottom: 14,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                }}
              >
                <FaInfoCircle />
                <span>Unable to get service fees</span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#7f1d1d",
                  lineHeight: 1.4,
                }}
              >
                Pricing for this service is currently unavailable. Please try
                again or contact support.
              </div>
              <button
                type="button"
                onClick={fetchPrices}
                style={{
                  alignSelf: "flex-start",
                  background: "none",
                  border: "none",
                  color: "#dc2626",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: 12,
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                Retry
              </button>
            </div>
          )}
          <PriceBreakdown>
            {pricingData &&
              (() => {
                const processingFees = unitProcessing;
                const taxCharges = unitTax;
                const perBureau = processingFees + taxCharges;
                const subtotal = perBureau * bureauMultiplier;
                const totalToPay = subtotal - discount;
                return (
                  <>
                    <PriceRow>
                      <span>
                        Processing fees
                        {bureauMultiplier > 1 ? ` × ${bureauMultiplier}` : ""}
                      </span>
                      <span>
                        {currencySymbol}
                        {(processingFees * bureauMultiplier).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                          },
                        )}
                      </span>
                    </PriceRow>
                    <PriceRow>
                      <span>
                        Tax & charges
                        {bureauMultiplier > 1 ? ` × ${bureauMultiplier}` : ""}
                      </span>
                      <span>
                        {currencySymbol}
                        {(taxCharges * bureauMultiplier).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                          },
                        )}
                      </span>
                    </PriceRow>
                    {discount > 0 && (
                      <PriceRow>
                        <span style={{ color: "#16a34a" }}>Discount</span>
                        <span style={{ color: "#16a34a" }}>
                          -{currencySymbol}
                          {discount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </PriceRow>
                    )}
                    <PriceTotalRow>
                      <span>Total to be paid</span>
                      <span>
                        {currencySymbol}
                        {totalToPay.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </PriceTotalRow>
                  </>
                );
              })()}
          </PriceBreakdown>
          <ContinueBtn
            onClick={handleContinueToPayment}
            disabled={
              !isFormValid() || loadingPrice || !!priceError || !pricingData
            }
            style={{ width: "100%", justifyContent: "center" }}
          >
            {loadingPrice ? (
              "Loading Price..."
            ) : priceError || !pricingData ? (
              "Price Unavailable"
            ) : (
              <>
                Continue to Payment <FaArrowRight />
              </>
            )}
          </ContinueBtn>
        </SidebarCard>
      </SearchGrid>

      <FormActions>
        <ClearBtn onClick={handleClear}>Clear</ClearBtn>
      </FormActions>
    </FormCard>
  );

  const renderCurrencySelector = () => {
    if (!canPayInNaira || !pricingData) return null;
    const baseAmount =
      ((pricingData.serviceFee || 0) +
        (pricingData.processingFee || 0) +
        (pricingData.vat || 0)) *
        bureauMultiplier -
      (allBureausSelected && config?.allBureausDiscount
        ? config.allBureausDiscount.usd
        : 0);
    const nairaAmount =
      nairaTotal * bureauMultiplier -
      (allBureausSelected && config?.allBureausDiscount
        ? config.allBureausDiscount.ngn
        : 0);
    const options = [
      {
        code: baseCurrency,
        label: `${baseCurrency} ($)`,
        amount: baseAmount,
        symbol: "$",
      },
      { code: "NGN", label: "Naira (₦)", amount: nairaAmount, symbol: "₦" },
    ];
    return (
      <div style={{ marginBottom: 14 }}>
        <PriceLabel>Pay in</PriceLabel>
        {options.map((opt) => {
          const checked = currencyCheck === opt.code;
          return (
            <label
              key={opt.code}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                marginBottom: 8,
                border: `1.5px solid ${checked ? "#09c93a" : "#e5e7eb"}`,
                borderRadius: 8,
                background: checked ? "#f0fdf4" : "#fafafa",
                cursor: "pointer",
                fontFamily: "Nunito, sans-serif",
                fontSize: 14,
                color: "#333",
              }}
            >
              <input
                type="radio"
                name="payCurrency"
                value={opt.code}
                checked={checked}
                onChange={() => setSelectedCurrency(opt.code)}
                style={{ accentColor: "#09c93a" }}
              />
              <span style={{ flex: 1 }}>{opt.label}</span>
              <strong>
                {opt.symbol}
                {opt.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </strong>
            </label>
          );
        })}
      </div>
    );
  };

  const renderPaymentStep = () => (
    <>
      <PaymentGrid>
        <PaymentMethodsCard>
          <PaymentMethodTitle>Payment Method</PaymentMethodTitle>
          <PaymentMethodSub>Choose how you'd like to pay</PaymentMethodSub>

          {PAYMENT_METHODS.map((method) => (
            <PaymentOption
              key={method.id}
              selected={paymentMethod === method.id}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.id}
                checked={paymentMethod === method.id}
                onChange={(e) => setPaymentMethod(e.target.value)}
              />
              {method.icon && (
                <method.icon style={{ fontSize: 16, color: "#555" }} />
              )}
              {method.id === "paystack" && (
                <img
                  src={paystackLogo}
                  alt="Paystack"
                  style={{ height: 16, objectFit: "contain" }}
                />
              )}
              {method.id === "flutterwave" && (
                <img
                  src={flutterwaveLogo}
                  alt="Flutterwave"
                  style={{ height: 16, objectFit: "contain" }}
                />
              )}
              <PaymentOptionLabel>{method.label}</PaymentOptionLabel>
              {method.id === "wallet" && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#777",
                    fontFamily: "Nunito",
                  }}
                >
                  {walletSymbol}
                  {userBalance.toLocaleString()}
                </span>
              )}
            </PaymentOption>
          ))}
        </PaymentMethodsCard>

        <SummaryCard>
          <SummaryHeader>
            <SummaryTitle>Verification Summary</SummaryTitle>
            <SummaryAmount>
              {loadingPrice
                ? "..."
                : priceError || !pricingData
                  ? "-"
                  : `${currencySymbol}${totalAmount.toLocaleString()}`}
            </SummaryAmount>
          </SummaryHeader>

          {renderCurrencySelector()}

          <SummaryRow>
            <SummaryLabel>Service</SummaryLabel>
            <SummaryValue>{config.serviceName}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Type</SummaryLabel>
            <SummaryValue>{config.idTypeLabel}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Value</SummaryLabel>
            <SummaryValue>
              {formData[config.serviceFieldKey] ||
                config.fields
                  .map((f) => formData[f.name])
                  .find((v) => v?.trim()) ||
                "—"}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Email</SummaryLabel>
            <SummaryValue>{userEmail || "—"}</SummaryValue>
          </SummaryRow>

          {pricingData && (
            <>
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  margin: "12px 0",
                }}
              />
              {config.bureaus && bureauCount > 0 && (
                <SummaryRow>
                  <SummaryLabel>Bureaus</SummaryLabel>
                  <SummaryValue>{bureauCount} selected</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow>
                <SummaryLabel>
                  Processing Fee
                  {bureauMultiplier > 1 ? ` × ${bureauMultiplier}` : ""}
                </SummaryLabel>
                <SummaryValue>
                  {currencySymbol}
                  {(unitProcessing * bureauMultiplier).toLocaleString()}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>
                  Tax & charges
                  {bureauMultiplier > 1 ? ` × ${bureauMultiplier}` : ""}
                </SummaryLabel>
                <SummaryValue>
                  {currencySymbol}
                  {(unitTax * bureauMultiplier).toLocaleString()}
                </SummaryValue>
              </SummaryRow>
              {discount > 0 && (
                <SummaryRow>
                  <SummaryLabel style={{ color: "#16a34a" }}>
                    Discount
                  </SummaryLabel>
                  <SummaryValue style={{ color: "#16a34a" }}>
                    -{currencySymbol}
                    {discount.toLocaleString()}
                  </SummaryValue>
                </SummaryRow>
              )}
            </>
          )}

          {/* Separator */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              margin: "16px 0 12px",
            }}
          />

          {/* Terms agreement checkbox */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              marginTop: 0,
              marginBottom: 12,
              border: `1.5px solid ${termsAccepted ? "#09c93a" : "#e5e7eb"}`,
              borderRadius: 8,
              cursor: "pointer",
              background: termsAccepted ? "#f0fdf4" : "#fafafa",
              transition: "all 0.2s",
              fontFamily: "Nunito, sans-serif",
              fontSize: 13,
              color: "#555",
            }}
          >
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{ accentColor: "#09c93a", width: 18, height: 18 }}
            />
            <span>
              I agree to the{" "}
              <span
                onClick={() => setShowTermsPopup(true)}
                style={{
                  color: "var(--ec-primary)",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Terms of Service
              </span>{" "}
              and{" "}
              <span
                onClick={() => setShowPrivacyPopup(true)}
                style={{
                  color: "var(--ec-primary)",
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Privacy Policy
              </span>
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              marginTop: 0,
              marginBottom: 12,
              border: `1.5px solid ${marketingAccepted ? "#09c93a" : "#e5e7eb"}`,
              borderRadius: 8,
              cursor: "pointer",
              background: marketingAccepted ? "#f0fdf4" : "#fafafa",
              transition: "all 0.2s",
              fontFamily: "Nunito, sans-serif",
              fontSize: 13,
              color: "#555",
            }}
          >
            <input
              type="checkbox"
              checked={marketingAccepted}
              onChange={(e) => setMarketingAccepted(e.target.checked)}
              style={{ accentColor: "#09c93a", width: 18, height: 18 }}
            />
            <span>
              Send me occasional offers and relevant e-citizen service
              recommendations.
            </span>
          </label>

          <PayBtn
            onClick={handlePay}
            disabled={
              !termsAccepted ||
              loading ||
              loadingPrice ||
              !!priceError ||
              !pricingData
            }
          >
            <FaLock />
            {loading
              ? "Processing..."
              : priceError || !pricingData
                ? "Price Unavailable"
                : `Pay ${currencySymbol}${totalAmount.toLocaleString()}`}
          </PayBtn>

          <SecuredBy>
            <FaShieldAlt style={{ color: "#09c93a" }} />
            Secured and encrypted payment
          </SecuredBy>

          <div style={{ marginTop: 16 }}>
            <ClearBtn onClick={handleBackToSearch} style={{ width: "100%" }}>
              <FaArrowLeft style={{ marginRight: 8 }} />
              Back
            </ClearBtn>
          </div>
        </SummaryCard>
      </PaymentGrid>

      {/* Sample Result */}
      <SampleSection>
        <SampleHeader>
          <SampleTitle>Sample Result</SampleTitle>
          <SampleBadge>This is a sample only</SampleBadge>
        </SampleHeader>
        <SampleSub>{sampleMeta.subtitle}</SampleSub>
        <div style={{ maxWidth: sampleType === "vehicle" ? 1180 : 760 }}>
          {sampleType === "vehicle" ? (
            <VehicleSampleReport hideFooter />
          ) : (
            <SampleResultContent type={sampleType} />
          )}
        </div>
      </SampleSection>
    </>
  );

  const renderProcessingStep = () => (
    <ProcessingWrapper>
      <ProcessingSpinner />
      <ProcessingText>Processing Your Verification</ProcessingText>
      <ProcessingSub>
        Please wait while we verify your information. This usually takes less
        than a minute.
      </ProcessingSub>
    </ProcessingWrapper>
  );

  const getResultPreviewFields = () => {
    if (!verificationResult?.data) return [];
    const data = verificationResult.data;
    const fields = [];

    // NIN / basic result (API returns lowercase: firstname, middlename, surname)
    if (data.firstname || data.firstName || data.surname || data.lastname) {
      const fname = data.firstname || data.firstName;
      const mname = data.middlename || data.middleName;
      const lname = data.surname || data.lastname;
      if (fname) fields.push({ label: "First Name", value: fname });
      if (mname) fields.push({ label: "Middle Name", value: mname });
      if (lname) fields.push({ label: "Last Name", value: lname });
      if (data.gender)
        fields.push({
          label: "Gender",
          value:
            data.gender === "m"
              ? "Male"
              : data.gender === "f"
                ? "Female"
                : data.gender,
        });
      if (data.birthDate || data.dateOfBirth || data.birthdate)
        fields.push({
          label: "Date of Birth",
          value: data.birthDate || data.dateOfBirth || data.birthdate,
        });
      if (data.telephoneno || data.telephoneNo || data.phone)
        fields.push({
          label: "Phone",
          value: data.telephoneno || data.telephoneNo || data.phone,
        });
      if (data.residenceAddress || data.residence_address)
        fields.push({
          label: "Address",
          value: data.residenceAddress || data.residence_address,
        });
    }

    // Vehicle verification
    if (data.vin || data.vehicleName) {
      if (data.vehicleName || data.name)
        fields.push({
          label: "Vehicle Name",
          value: data.vehicleName || data.name,
        });
      if (data.vin) fields.push({ label: "VIN", value: data.vin });
      if (data.year) fields.push({ label: "Year", value: data.year });
      if (data.make) fields.push({ label: "Make", value: data.make });
      if (data.model) fields.push({ label: "Model", value: data.model });
      if (data.trim) fields.push({ label: "Trim", value: data.trim });
      if (data.engine) fields.push({ label: "Engine", value: data.engine });
      if (data.fuelType)
        fields.push({ label: "Fuel Type", value: data.fuelType });
      if (data.transmission)
        fields.push({ label: "Transmission", value: data.transmission });
      if (data.verificationStatus)
        fields.push({
          label: "Verification Status",
          value: data.verificationStatus,
        });
      if (data.reference)
        fields.push({ label: "Reference", value: data.reference });
    }

    // Business (API returns: approvedName, rcNumber, registrationDate, address, email, lga, state, classificationId)
    if (data.approvedName || data.companyName || data.company_name) {
      fields.push({
        label: "Business Name",
        value: data.approvedName || data.companyName || data.company_name,
      });
      if (data.rcNumber || data.rc_number)
        fields.push({
          label: "RC Number",
          value: data.rcNumber || data.rc_number,
        });
      if (data.registrationDate)
        fields.push({
          label: "Registration Date",
          value: new Date(data.registrationDate).toLocaleDateString(),
        });
      if (data.address && data.address !== "null")
        fields.push({ label: "Address", value: data.address });
      if (data.state && data.state !== "null")
        fields.push({ label: "State", value: data.state });
      if (data.lga && data.lga !== "null")
        fields.push({ label: "LGA", value: data.lga });
      if (data.email && data.email !== "null")
        fields.push({ label: "Email", value: data.email });
      if (data.companyStatus)
        fields.push({ label: "Status", value: data.companyStatus });
    }

    // Credit bureau
    if (data.advance || data.crc || data.firstCentral || data.creditRegistry) {
      if (data.crc) fields.push({ label: "CRC", value: "Data received" });
      if (data.firstCentral)
        fields.push({ label: "First Central", value: "Data received" });
      if (data.creditRegistry)
        fields.push({ label: "Credit Registry", value: "Data received" });
    }

    // Generic fallback — show first few string fields
    if (fields.length === 0) {
      Object.entries(data)
        .slice(0, 5)
        .forEach(([key, val]) => {
          if (
            typeof val === "string" &&
            val &&
            val !== "null" &&
            key !== "status" &&
            key !== "detail" &&
            key !== "photo" &&
            key !== "signature" &&
            key !== "rawData"
          ) {
            fields.push({
              label: key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (s) => s.toUpperCase()),
              value: val,
            });
          }
        });
    }

    return fields.slice(0, 8); // Show max 8 fields
  };

  const renderConsentStep = () => (
    <FormCard>
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <ProcessingSpinner />
        <ProcessingText style={{ marginTop: 24 }}>
          Awaiting Consent
        </ProcessingText>
        <ProcessingSub
          style={{ maxWidth: 520, margin: "12px auto 0", lineHeight: 1.7 }}
        >
          We have sent a consent request to the data subject and are currently
          awaiting their response. An email will be sent to you regarding the
          status of your request.
        </ProcessingSub>
        <ProcessingSub
          style={{
            maxWidth: 520,
            margin: "16px auto 0",
            fontSize: 13,
            color: "#999",
          }}
        >
          The data subject's information will be retained for 24 hours from the
          moment they grant consent. This page will automatically update when
          consent is granted.
        </ProcessingSub>
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            marginTop: 32,
          }}
        >
          <ClearBtn onClick={() => history.push("/main-dashboard")}>
            Return to Dashboard
          </ClearBtn>
        </div>
      </div>
    </FormCard>
  );

  const renderResultStep = () => {
    const previewFields = getResultPreviewFields();
    const resultRoute = verificationResult?.route || "/main-dashboard";
    const resultTitle = verificationResult?.title || "Verification Complete!";
    const resultDetail = verificationResult?.detail || "";
    const bureauResults = verificationResult?.data?.bureauResults || [];
    const bureauErrors = verificationResult?.data?.bureauErrors || [];

    return (
      <FormCard>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#e6f9ed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FaCheckCircle style={{ fontSize: 28, color: "#09c93a" }} />
          </div>
          <ProcessingText>{resultTitle}</ProcessingText>
          <ProcessingSub>
            {resultDetail ||
              "Your verification has been completed successfully."}
          </ProcessingSub>
        </div>

        {/* Summary of what was submitted */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "20px 24px",
            marginBottom: 20,
          }}
        >
          <h4
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: 15,
              color: "#354138",
              margin: "0 0 16px",
            }}
          >
            Verification Summary
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 24px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontFamily: "Nunito, sans-serif",
                  marginBottom: 2,
                }}
              >
                Service
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#333",
                  fontFamily: "Nunito, sans-serif",
                  fontWeight: 600,
                }}
              >
                {config.serviceName}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontFamily: "Nunito, sans-serif",
                  marginBottom: 2,
                }}
              >
                Amount Paid
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#333",
                  fontFamily: "Nunito, sans-serif",
                  fontWeight: 600,
                }}
              >
                {currencySymbol}
                {totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            </div>
            {config.fields.map((field) =>
              formData[field.name]?.trim() ? (
                <div key={field.name}>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#999",
                      fontFamily: "Nunito, sans-serif",
                      marginBottom: 2,
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#333",
                      fontFamily: "Nunito, sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {formData[field.name]}
                  </div>
                </div>
              ) : null,
            )}
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontFamily: "Nunito, sans-serif",
                  marginBottom: 2,
                }}
              >
                Status
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#09c93a",
                  fontFamily: "Nunito, sans-serif",
                  fontWeight: 700,
                }}
              >
                <FaCheckCircle
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                Successful
              </div>
            </div>
          </div>
        </div>

        {/* Bureau-specific results */}
        {(bureauResults.length > 0 || bureauErrors.length > 0) && (
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "20px 24px",
              marginBottom: 20,
            }}
          >
            <h4
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 15,
                color: "#354138",
                margin: "0 0 12px",
              }}
            >
              Bureau Results
            </h4>
            {bureauResults.map((bureau, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <FaCheckCircle style={{ color: "#09c93a", fontSize: 14 }} />
                <span
                  style={{
                    fontSize: 14,
                    fontFamily: "Nunito, sans-serif",
                    color: "#333",
                  }}
                >
                  {bureau} — Data received
                </span>
              </div>
            ))}
            {bureauErrors.map((err, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <span style={{ color: "#dc2626", fontSize: 14 }}>✕</span>
                <span
                  style={{
                    fontSize: 14,
                    fontFamily: "Nunito, sans-serif",
                    color: "#dc2626",
                  }}
                >
                  {err}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Preview fields from response data */}
        {previewFields.length > 0 && (
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "20px 24px",
              marginBottom: 24,
            }}
          >
            <h4
              style={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: 15,
                color: "#354138",
                margin: "0 0 16px",
              }}
            >
              Result Preview
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px 24px",
              }}
            >
              {previewFields.map((field, i) => (
                <div key={i}>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#999",
                      fontFamily: "Nunito, sans-serif",
                      marginBottom: 2,
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#333",
                      fontFamily: "Nunito, sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    {field.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <ContinueBtn
            onClick={() => history.push(resultRoute)}
            style={{ justifyContent: "center" }}
          >
            View Full Results <FaArrowRight />
          </ContinueBtn>
          <ClearBtn onClick={() => history.push("/main-dashboard")}>
            Go to Dashboard
          </ClearBtn>
        </div>
      </FormCard>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderSearchStep();
      case 1:
        return renderPaymentStep();
      case 2:
        return renderProcessingStep();
      case 3:
        return requiresConsent ? renderConsentStep() : renderResultStep();
      case 4:
        return renderResultStep();
      default:
        return renderSearchStep();
    }
  };

  return (
    <PageWrapper>
      {/* Hero */}
      <HeroSection>
        <Breadcrumb>
          <span>Home</span> / <span>{config.breadcrumb[0]}</span> /{" "}
          <span>{config.breadcrumb[1]}</span>
        </Breadcrumb>
        <HeroInner>
          <HeroText>
            <HeroTitle>
              {config.heroTitle} <span>{config.heroHighlight}</span>
              {config.heroContinue}
            </HeroTitle>
            <HeroSubtitle>{config.heroSubtitle}</HeroSubtitle>
          </HeroText>
          <HeroImage src={config.heroImage} alt={config.serviceName} />
        </HeroInner>
      </HeroSection>

      {/* Stepper */}
      {renderStepper()}

      {/* Content */}
      <ContentWrapper>
        {renderStepContent()}
        <div style={{ display: "none" }}>
          <RecommendedOffers variant="green" />
        </div>
      </ContentWrapper>

      {/* Result Popup - only for non-consent verifications (business, vehicle) */}
      {showResultPopup && verificationResult && !isConsentVerification && (
        <PopupOverlay>
          <PopupCard>
            <PopupHeader>
              <PopupMeta>
                <PopupIcon>
                  <FaCheckCircle />
                </PopupIcon>
                <div>
                  <PopupTitle>{verificationResult.title}</PopupTitle>
                  <PopupSubtitle>{verificationResult.detail}</PopupSubtitle>
                </div>
              </PopupMeta>
              <PopupCloseButton
                onClick={() => {
                  setShowResultPopup(false);
                  setCurrentStep(RESULT_STEP);
                }}
              >
                ×
              </PopupCloseButton>
            </PopupHeader>
            <PopupBody>
              {/* Business list result */}
              {verificationResult.data?._isBusinessList ? (
                <>
                  <PopupCountBanner>
                    <FaBuilding />
                    {verificationResult.data.businesses.length}{" "}
                    {verificationResult.data.businesses.length === 1
                      ? "company"
                      : "companies"}{" "}
                    found
                  </PopupCountBanner>
                  <PopupBusinessList>
                    {verificationResult.data.businesses.map((biz, idx) => (
                      <PopupBusinessItem key={idx}>
                        <PopupBusinessIcon>
                          <FaBuilding />
                        </PopupBusinessIcon>
                        <PopupBusinessInfo>
                          <PopupBusinessName>
                            {biz.approvedName || biz.companyName || "N/A"}
                          </PopupBusinessName>
                          <PopupBusinessMeta>
                            {(biz.rcNumber || biz.rc_number) && (
                              <span style={{ color: "var(--ec-text-muted)" }}>
                                RC:{" "}
                                <strong style={{ color: "var(--ec-text)" }}>
                                  {biz.rcNumber || biz.rc_number}
                                </strong>
                              </span>
                            )}
                            {biz.registrationDate && (
                              <span style={{ color: "var(--ec-text-muted)" }}>
                                Reg:{" "}
                                <strong style={{ color: "var(--ec-text)" }}>
                                  {new Date(
                                    biz.registrationDate,
                                  ).toLocaleDateString()}
                                </strong>
                              </span>
                            )}
                            {biz.classificationId && (
                              <span style={{ color: "var(--ec-text-muted)" }}>
                                Type:{" "}
                                <strong style={{ color: "var(--ec-text)" }}>
                                  {biz.classificationId === "1"
                                    ? "Business Name"
                                    : biz.classificationId === "2"
                                      ? "Limited Company"
                                      : biz.classificationId}
                                </strong>
                              </span>
                            )}
                          </PopupBusinessMeta>
                        </PopupBusinessInfo>
                        <VerifiedBadgePopup
                          style={{ flexShrink: 0, alignSelf: "center" }}
                        >
                          <FaCheckCircle /> Verified
                        </VerifiedBadgePopup>
                      </PopupBusinessItem>
                    ))}
                  </PopupBusinessList>
                  <ResultFooterPopup
                    style={{ borderTop: "none", padding: "0 0 12px" }}
                  >
                    <span>
                      Verified on{" "}
                      {new Date().toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span>
                      Ref: {localStorage.getItem("transactionID") || "N/A"}
                    </span>
                  </ResultFooterPopup>
                </>
              ) : (
                /* Non-business single result */
                <ResultCardPopup>
                  <ResultTopPopup>
                    <ResultPhotoPopup>
                      {(() => {
                        const data = verificationResult?.data;
                        if (!data) return <FaUserCircle />;
                        let photoSrc =
                          data.vehicleImage ||
                          data.previewImageURL ||
                          data.photo ||
                          data.signature ||
                          data.image ||
                          data.profilePhoto ||
                          data.profilePic ||
                          data.picture ||
                          data.biometricPhoto ||
                          data.facialImage ||
                          data.faceImage ||
                          data.photoUrl ||
                          data.imageUrl;
                        if (photoSrc) {
                          if (
                            !photoSrc.startsWith("data:") &&
                            !photoSrc.startsWith("http")
                          ) {
                            if (
                              photoSrc.startsWith("/9j/") ||
                              photoSrc.startsWith("iVBORw0KGgo")
                            ) {
                              const mimeType = photoSrc.startsWith("/9j/")
                                ? "image/jpeg"
                                : "image/png";
                              photoSrc = `data:${mimeType};base64,${photoSrc}`;
                            }
                          }
                          return (
                            <img
                              src={photoSrc}
                              alt="Verification photo"
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          );
                        }
                        return <FaUserCircle />;
                      })()}
                    </ResultPhotoPopup>
                    <ResultGridPopup>
                      {getResultPreviewFields().map((field, idx) => (
                        <ResultFieldPopup key={idx}>
                          <ResultLabelPopup>{field.label}</ResultLabelPopup>
                          {field.label === "Verification Status" ? (
                            <VerifiedBadgePopup>
                              VERIFIED <FaCheckCircle />
                            </VerifiedBadgePopup>
                          ) : (
                            <ResultValuePopup>{field.value}</ResultValuePopup>
                          )}
                        </ResultFieldPopup>
                      ))}
                    </ResultGridPopup>
                  </ResultTopPopup>
                  <ResultFooterPopup>
                    <span>
                      Verified on{" "}
                      {new Date().toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span>
                      Ref: {localStorage.getItem("transactionID") || "N/A"}
                    </span>
                  </ResultFooterPopup>
                </ResultCardPopup>
              )}
              <ResultDisclaimerPopup>
                <FaInfoCircle />
                Results are based on data available at the time of verification.
              </ResultDisclaimerPopup>
              <PopupActionRow>
                <ContinueBtn
                  onClick={() => {
                    setShowResultPopup(false);
                    history.push("/main-dashboard");
                  }}
                >
                  View full result <FaArrowRight />
                </ContinueBtn>
                <ClearBtn
                  onClick={() => {
                    setShowResultPopup(false);
                    setCurrentStep(RESULT_STEP);
                  }}
                >
                  Close
                </ClearBtn>
              </PopupActionRow>
            </PopupBody>
          </PopupCard>
        </PopupOverlay>
      )}
      <TrustBar>
        <TrustBarInner>
          {[
            {
              icon: FaShieldAlt,
              ...config.trustBar[0],
            },
            {
              icon: FaBolt,
              ...config.trustBar[1],
            },
            {
              icon: FaCheckCircle,
              ...config.trustBar[2],
            },
            {
              icon: FaUsers,
              ...config.trustBar[3],
            },
          ].map((item, i) => (
            <TrustItem key={i}>
              <TrustIcon>
                <item.icon />
              </TrustIcon>
              <TrustText>
                <TrustTitle>{item.title}</TrustTitle>
                <TrustDesc>{item.desc}</TrustDesc>
              </TrustText>
            </TrustItem>
          ))}
        </TrustBarInner>
      </TrustBar>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <PopupOverlay>
          <PopupCard style={{ maxWidth: 560 }}>
            <PopupHeader>
              <PopupMeta>
                <PopupIcon
                  style={{
                    background: "rgba(235, 3, 24, 0.10)",
                    color: "#EB0318",
                  }}
                >
                  <FaInfoCircle />
                </PopupIcon>
                <div>
                  <PopupTitle>Disclaimer</PopupTitle>
                  <PopupSubtitle>Please review before proceeding</PopupSubtitle>
                </div>
              </PopupMeta>
              <PopupCloseButton onClick={() => setShowDisclaimer(false)}>
                ×
              </PopupCloseButton>
            </PopupHeader>
            <PopupBody>
              <div
                style={{
                  background: "rgba(235, 3, 24, 0.06)",
                  border: "1px solid rgba(235, 3, 24, 0.2)",
                  borderRadius: 10,
                  padding: "16px 20px",
                  marginBottom: 20,
                }}
              >
                {type === "vehicle" ? (
                  /* ── Vehicle (VIN / ClearVin) disclaimer ── */
                  <div
                    style={{
                      fontFamily: "Nunito, sans-serif",
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: "var(--ec-text)",
                    }}
                  >
                    By clicking, you indicate that:
                    <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                      <li style={{ marginBottom: 12 }}>
                        You confirm that search details are correct, and you
                        confirm that you will <strong>not be refunded</strong>{" "}
                        for incorrect information.
                      </li>
                      <li style={{ marginBottom: 12 }}>
                        You understand and accept that vehicle history data is
                        sourced from third-party providers and{" "}
                        <strong>may not contain all records</strong> for every
                        vehicle.
                      </li>
                      <li style={{ marginBottom: 0 }}>
                        You understand that{" "}
                        <strong>
                          search results may come back without any data
                        </strong>
                        , and you accept that you will not be refunded.
                      </li>
                    </ul>
                  </div>
                ) : (
                  /* ── NIN / Phone / Business / BVN disclaimer ── */
                  <ol
                    style={{
                      margin: 0,
                      paddingLeft: 20,
                      fontFamily: "Nunito, sans-serif",
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: "var(--ec-text)",
                    }}
                  >
                    {(type === "nin" || type === "phone" || type === "bvn") && (
                      <li style={{ marginBottom: 12 }}>
                        You confirm that you understand and accept that{" "}
                        <strong>consent is required</strong> from the data
                        subject being verified before you can access their data,
                        and you accept that you will not be refunded if consent
                        is withheld.
                      </li>
                    )}
                    <li style={{ marginBottom: 12 }}>
                      You confirm and accept that the{" "}
                      <strong>search details are correct</strong>, and you
                      accept that you will not be refunded for incorrect
                      information.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      You understand and accept that{" "}
                      <strong>
                        search details may come back without any data
                      </strong>
                      , and you accept that you will not be refunded.
                    </li>
                  </ol>
                )}
              </div>

              <PopupActionRow style={{ justifyContent: "center" }}>
                <ContinueBtn onClick={handleDisclaimerConfirm}>
                  I Understand, Continue <FaArrowRight />
                </ContinueBtn>
                <ClearBtn onClick={() => setShowDisclaimer(false)}>
                  Cancel
                </ClearBtn>
              </PopupActionRow>
            </PopupBody>
          </PopupCard>
        </PopupOverlay>
      )}

      <PdfModal
        open={showTermsPopup}
        onClose={() => setShowTermsPopup(false)}
        title="Terms of Service"
        src={termsPdf}
        height={420}
      />
      <PdfModal
        open={showPrivacyPopup}
        onClose={() => setShowPrivacyPopup(false)}
        title="Privacy Policy"
        src={privacyPdf}
        height={420}
      />

      {/* Paystack Payment Modal */}
      {paystackModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "95%",
              maxWidth: 900,
              maxHeight: "92vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#354138",
                }}
              >
                Complete Payment —{" "}
                {activeGateway === "flutterwave" ? "Flutterwave" : "Paystack"}
              </h3>
              <button
                onClick={handlePaystackModalClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "#999",
                  padding: "4px 8px",
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              title={
                activeGateway === "flutterwave"
                  ? "Flutterwave Payment"
                  : "Paystack Payment"
              }
              src={paymentUrl}
              style={{
                width: "100%",
                height: 600,
                border: "none",
              }}
            />
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default VerifyPage;
